import { useSeo, SITE_URL } from "@/lib/use-seo";
import { DemoVideo } from "@/components/ui/DemoVideo";
import styles from "./home.module.css";

/**
 * The apex identity page (#221).
 *
 * This is not a marketing page and the "no marketing pages in web/" rule
 * still holds: it exists because the product's Customer-facing links — quote
 * approvals, deposit and balance invoices — are sent on behalf of a Company
 * whose brand the domain does not own, reached by an opaque `?token=` link.
 * A domain whose root does not resolve, doing that, is what Google Safe
 * Browsing reads as social engineering; it interstitialled the token pages
 * with a red "Dangerous site" warning on 2026-07-26.
 *
 * So this page has one job: say plainly who runs the domain, what it sends,
 * and how to reach a human. It is deliberately short — every claim on it must
 * stay true without maintenance.
 *
 * The demo video serves that same job rather than cutting against it: a
 * reader checking whether an emailed link is genuine is answered better by
 * three minutes of the product working than by another paragraph asserting
 * it. It is one video and it stays one video — a second, or a feature grid
 * beside it, is the marketing page this file's rule bans.
 *
 * The page is laid out as three things in one column: who runs the domain,
 * what the product is, and what to do about a link. "Got a link from us?"
 * sits below the video but keeps its own heading and hairline, so the
 * worried reader — the one this page was built for — can find it by
 * scanning rather than by reading. The container is wider than the prose
 * measure because a 1080p screen recording at reading width is unreadable;
 * every text block inside it is still capped at that measure.
 *
 * Tenant subdomains never render this: `/` there is the inquiry flow.
 */

/** The demo on YouTube. Unlisted is fine — the embed does not need it listed. */
const DEMO_VIDEO_ID = "JEQ0hwLeYV4";

const DEMO_TITLE = "How HireOS turns a phone call into a quote";

export default function HomePage() {
  useSeo({
    title: "HireOS: enquiry software for rental equipment companies",
    description:
      "Software that deals with all enquiries to a rental equipment company. We pick up when nobody else can, by phone, email or form, and whatever gets agreed goes into the RMS that company already uses as a draft: a new booking, or a change to one it already has.",
    canonical: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: DEMO_TITLE,
        description:
          "A walkthrough of HireOS: a customer rings a rental company's number, the AI receptionist takes the job, and a priced draft quote lands in the company's own rental system.",
        thumbnailUrl: `${SITE_URL}/demo-poster.jpg`,
        uploadDate: "2026-09-12",
        duration: "PT2M50S",
        embedUrl: `https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}`,
      },
    ],
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.wordmark}>HireOS</span>
        </div>
      </header>

      <main className={styles.main}>
        <h1 className={styles.headline}>
          Software that deals with communications for hire companies.
        </h1>
        <ol className={styles.steps}>
          {[
            "We pick up when nobody else can. Phone, email, your form, any hour.",
            "Whatever gets agreed goes into the RMS you already use, as a draft waiting on you. A new booking, or a change to one you already have.",
            "Every call, email and form sits on the enquiry thread, recordings and transcripts included, so you can see exactly what was said.",
          ].map((step, i) => (
            <li key={step} className={styles.step}>
              <span className={styles.stepNumber}>{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <section className={styles.demo}>
          <DemoVideo youtubeId={DEMO_VIDEO_ID} title={DEMO_TITLE} />
        </section>

        <section className={styles.linkNotice}>
          <h2 className={styles.noticeHeading}>Got a link from us?</h2>
          <p className={styles.noticeBody}>
            Quotes, approvals and invoices on this domain are sent by HireOS for
            the rental company named on the page. The name and bank details you
            see are that company's, not ours. We are not the company you are
            hiring from, and we never take payment.
          </p>
          <p className={styles.noticeBody}>
            We will never ask for a password, a card number, or your banking
            login. If a page claiming to be us does, don't fill it in. Email{" "}
            <a href="mailto:hello@loony.io" className={styles.link}>
              hello@loony.io
            </a>{" "}
            and we'll check it.
          </p>
        </section>

        <footer className={styles.footer}>
          <a href="/privacy" className={styles.link}>
            Privacy
          </a>
          <span className={styles.footerSeparator}>·</span>
          <a href="/terms" className={styles.link}>
            Terms
          </a>
        </footer>
      </main>
    </div>
  );
}
